/**
 * Data-fetching helpers over TanStack Query.
 * After any successful mutation all cached queries are invalidated, so totals,
 * dashboards and lists everywhere reflect the change immediately.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Params } from "./api.ts";

export function useApi<T>(path: string | null, params?: Params, opts: { keepPrevious?: boolean } = {}) {
  return useQuery({
    queryKey: [path, params ?? {}],
    queryFn: () => api.get<T>(path!, params),
    enabled: path !== null,
    placeholderData: opts.keepPrevious ? keepPreviousData : undefined,
  });
}

type Method = "post" | "put" | "delete";

export function useApiMutation<TBody = unknown, TResult = unknown>(
  method: Method,
  path: string | ((body: TBody) => string),
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) => {
      const url = typeof path === "function" ? path(body) : path;
      if (method === "delete") return api.delete<TResult>(url);
      return api[method]<TResult>(url, body);
    },
    onSuccess: () => client.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "auth" }),
  });
}

/** Invalidate everything except auth state (after uploads or custom calls). */
export function useInvalidateAll() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "auth" });
}
